import type { QCIssue, RuleId, Severity } from '../../core';

/**
 * Cue-table columns a QC issue can point at. The `dur`/`cps` cells are
 * deliberately absent: their over-threshold tinting is owned by the metric
 * threshold display (cue-forge-n3e.4), which is in lockstep with the
 * `max-cps`/`min-duration`/`max-duration` rules, so flagging them again here
 * would only double-paint the same cell.
 */
export type FlagColumn = 'start' | 'end' | 'text';

/** Per-cue rollup of the live QC issues, used to mark rows in the cue grid. */
export interface RowIssueSummary {
  /** Worst severity across all of the cue's issues (error beats warning). */
  severity: Severity;
  /** Total number of issues on the cue. */
  count: number;
  /** Worst severity per offending column, for the cell tint. */
  cells: Partial<Record<FlagColumn, Severity>>;
}

/** error > warning, so a row/cell shows the more urgent state. */
function worst(a: Severity, b: Severity): Severity {
  return a === 'error' || b === 'error' ? 'error' : 'warning';
}

/**
 * The cue-table column(s) a rule concerns, so the grid can tint the offending
 * cell. Timing rules point at the boundary that is wrong; everything textual
 * points at the text cell. CPS/duration return none (see {@link FlagColumn}).
 */
export function ruleColumns(ruleId: RuleId): FlagColumn[] {
  switch (ruleId) {
    case 'overlap':
    case 'min-gap':
      return ['start'];
    case 'reversed-timing':
      return ['start', 'end'];
    case 'max-cps':
    case 'min-duration':
    case 'max-duration':
    case 'cue-count-monotonic-timing':
      return [];
    default:
      // Every remaining rule is about the cue's text content.
      return ['text'];
  }
}

/**
 * Group QC issues by their cue index (the 1-based `Cue.index`) into a per-row
 * summary the cue grid can render inline. Document-level issues (cueIndex -1)
 * have no row and are dropped.
 */
export function summarizeRowIssues(issues: QCIssue[]): Map<number, RowIssueSummary> {
  const map = new Map<number, RowIssueSummary>();
  for (const issue of issues) {
    if (issue.cueIndex < 0) continue;
    let s = map.get(issue.cueIndex);
    if (!s) {
      s = { severity: issue.severity, count: 0, cells: {} };
      map.set(issue.cueIndex, s);
    }
    s.count += 1;
    s.severity = worst(s.severity, issue.severity);
    for (const col of ruleColumns(issue.ruleId)) {
      const prev = s.cells[col];
      s.cells[col] = prev ? worst(prev, issue.severity) : issue.severity;
    }
  }
  return map;
}

/** Accessible description of a row's flag for the index-gutter marker. */
export function rowFlagLabel(s: RowIssueSummary): string {
  const noun = s.count === 1 ? 'issue' : 'issues';
  const sev = s.count === 1 ? s.severity : `worst ${s.severity}`;
  return `${s.count} ${noun}, ${sev}`;
}
